import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { setCache, getCache } from "../../lib/offline";
import BlockScout from "./BlockScout";
import { useAuth } from "../../context/AuthContext";
import { ArrowLeft, CheckCircle2, Circle, ClipboardList } from "lucide-react";
import { bLabel } from "./parts";

export default function VisitFlow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bundle, setBundle] = useState(null);
  const [activeBlock, setActiveBlock] = useState(null);
  const [summary, setSummary] = useState(null);
  const [completing, setCompleting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/scouting/visits/${id}`);
      setBundle(data);
      setCache(`scout_visit:${id}`, data);
      return data;
    } catch (e) {
      const cached = await getCache(`scout_visit:${id}`);
      if (cached) { setBundle((b) => b || cached); return cached; }
      return null;
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const applyLocal = (ranchId, obsDoc) => {
    setBundle((b) => {
      if (!b) return b;
      const observations = [...b.observations.filter((o) => o.ranch_id !== ranchId), obsDoc];
      const blocks_done = b.visit.blocks_done.includes(ranchId) ? b.visit.blocks_done : [...b.visit.blocks_done, ranchId];
      const nb = { ...b, observations, visit: { ...b.visit, blocks_done } };
      setCache(`scout_visit:${id}`, nb);
      return nb;
    });
  };

  if (!bundle) return <div className="p-8 font-bold uppercase text-sm">Loading…</div>;
  const { visit, blocks, observations, photos, tasks } = bundle;
  const done = visit.blocks_done || [];
  const ordered = [...blocks].sort((a, b) => String(a.block || a.name).localeCompare(String(b.block || b.name), undefined, { numeric: true }));
  const obsFor = (rid) => observations.find((o) => o.ranch_id === rid);

  const openSummary = async () => {
    const { data } = await api.get(`/scouting/visits/${id}/summary`);
    setSummary(data);
  };

  const completeVisit = async () => {
    setCompleting(true);
    try {
      await api.post(`/scouting/visits/${id}/status`, { status: "scout_complete" });
      if (["admin", "supervisor"].includes(user.role)) navigate(`/scouting/review/${id}`);
      else navigate("/scouting");
    } catch (e) {
      if (!e.response || e.response.status === 503) alert("You're offline. Your blocks are saved and will sync — complete the visit once you're back online.");
    } finally { setCompleting(false); }
  };

  if (activeBlock) {
    const idx = ordered.findIndex((b) => b.id === activeBlock.id);
    const next = ordered[idx + 1];
    return (
      <div className="space-y-3" data-testid="visit-flow-block">
        <div className="flex items-center justify-between">
          <button data-testid="back-to-blocks" onClick={async () => { await load(); setActiveBlock(null); }} className="h-11 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><ArrowLeft size={15} /> Blocks</button>
          <span className="text-sm font-bold uppercase text-stone-500">{done.length} of {ordered.length} completed</span>
        </div>
        <BlockScout visit={visit} block={activeBlock} photos={photos} existingObs={obsFor(activeBlock.id)}
          hasNext={!!next}
          onSaved={load}
          onLocalSaved={applyLocal}
          onNext={async () => { await load(); setActiveBlock(next); }} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="visit-flow">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/scouting")} className="h-11 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><ArrowLeft size={15} /> Scouting</button>
      </div>
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{visit.vineyard_name}</h1>
        <div className="text-stone-500 text-sm mt-1">
          {[visit.client_name, visit.visit_date, `Scout: ${visit.scout_name}`].filter(Boolean).join(" · ")}
          <span className="ml-2 uppercase text-xs font-bold px-2 py-0.5 bg-[#1F3D34] text-white">{visit.status.replace("_", " ")}</span>
        </div>
      </div>

      <div className="bg-white border border-stone-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="font-bold uppercase text-sm">Blocks</span>
          <span data-testid="blocks-progress" className="text-sm font-bold text-[#1F3D34]">{done.length} OF {ordered.length} BLOCKS COMPLETED</span>
        </div>
        <div className="h-2 bg-stone-100 mb-4"><div className="h-2 bg-[#7C9673]" style={{ width: `${ordered.length ? (done.length / ordered.length) * 100 : 0}%` }} /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {ordered.map((b) => {
            const isDone = done.includes(b.id);
            return (
              <button key={b.id} data-testid={`block-card-${b.id}`} onClick={() => setActiveBlock(b)}
                className={`text-left p-4 border flex items-center justify-between gap-2 transition-colors ${isDone ? "border-[#7C9673] bg-[#7C9673]/10" : "border-stone-200 bg-white hover:border-[#1F3D34]"}`}>
                <div>
                  <div className="font-bold">{b.block ? bLabel(b.block) : b.name}</div>
                  <div className="text-xs text-stone-500">{[b.variety, b.acres ? `${b.acres} ac` : null].filter(Boolean).join(" · ") || b.name}</div>
                </div>
                {isDone ? <CheckCircle2 className="text-[#1F3D34]" size={22} /> : <Circle className="text-stone-300" size={22} />}
              </button>
            );
          })}
        </div>
      </div>

      {tasks.length > 0 && (
        <div className="bg-white border border-stone-200 p-4">
          <div className="font-bold uppercase text-sm mb-2 flex items-center gap-2"><ClipboardList size={16} /> Tasks Created This Visit ({tasks.length})</div>
          {tasks.map((t) => (
            <div key={t.id} className="flex justify-between text-sm py-1 border-b border-stone-100 last:border-0">
              <span>{t.title} <span className="text-xs text-stone-400">· {t.block_labels?.join(", ")}</span></span>
              <span className="text-xs uppercase font-bold text-stone-500">{t.category}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <button data-testid="save-draft-btn" onClick={() => navigate("/scouting")} className="flex-1 h-14 bg-white border border-stone-300 font-bold uppercase">Save Draft</button>
        <button data-testid="open-summary-btn" onClick={openSummary} disabled={done.length === 0}
          className="flex-1 h-14 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40">Complete Scouting</button>
      </div>

      {summary && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="scouting-summary-modal">
            <h3 className="text-2xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Scouting Summary</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[["Vineyard", visit.vineyard_name], ["Blocks inspected", `${summary.blocks_done} / ${summary.blocks_total}`],
                ["Acres", summary.acres], ["Pest/disease issues", summary.pest_issues],
                ["Irrigation issues", summary.irrigation_issues], ["Health issues", summary.health_issues],
                ["Follow-ups flagged", summary.followups], ["Tasks created", summary.tasks_total],
                ["Photos", summary.photos]].map(([k, v]) => (
                <div key={k} className="border border-stone-200 p-2"><div className="text-[10px] uppercase font-bold text-stone-500">{k}</div><div className="font-bold text-lg">{v}</div></div>
              ))}
            </div>
            {Object.keys(summary.tasks_by_category || {}).length > 0 && (
              <div className="text-sm">
                <div className="font-bold uppercase text-xs mb-1">Tasks by type</div>
                {Object.entries(summary.tasks_by_category).map(([k, v]) => <div key={k} className="flex justify-between border-b border-stone-100 py-0.5"><span>{k}</span><b>{v}</b></div>)}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setSummary(null)} className="flex-1 h-12 border border-stone-300 font-bold uppercase bg-white">Keep Scouting</button>
              <button data-testid="confirm-complete-btn" onClick={completeVisit} disabled={completing} className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40">
                {completing ? "Completing…" : "Complete Scouting"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
